/**
 * Guided options progress (variant options + personalization), capped at 99% until add to bag.
 * Variant steps only count after the shopper engages with each option (defaults alone do not fill the bar).
 * One instance per [data-product-options-progress-root].
 */
(function () {
  'use strict';

  const CAP = 99;

  function decodeProgressCopy(str) {
    if (!str || typeof str !== 'string') {
      return str;
    }
    return str
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&#x27;/gi, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  function normalizeHintsPayload(parsed) {
    if (!parsed || typeof parsed !== 'object') {
      return { steps: [], complete: '' };
    }
    const steps = Array.isArray(parsed.steps) ? parsed.steps : [];
    const complete = typeof parsed.complete === 'string' ? parsed.complete : '';
    return {
      steps: steps.map(function (s) {
        return typeof s === 'string' ? decodeProgressCopy(s) : '';
      }),
      complete: decodeProgressCopy(complete),
    };
  }

  function getVariantGroups(form) {
    const groups = [];
    const radios = form.querySelector('variant-radios');
    if (radios) {
      for (const fs of radios.querySelectorAll('fieldset.product-form__input[data-option-index]')) {
        groups.push({ type: 'radio', el: fs });
      }
      return groups;
    }
    const selects = form.querySelector('variant-selects');
    if (selects) {
      for (const wrap of selects.querySelectorAll('.product-form__input--dropdown')) {
        const sel = wrap.querySelector('select');
        if (sel) {
          groups.push({ type: 'select', el: sel });
        }
      }
    }
    return groups;
  }

  function isVariantGroupDone(group) {
    if (group.type === 'radio') {
      return Boolean(group.el.querySelector('input[type="radio"]:checked:not(.disabled)')) ||
        Boolean(group.el.querySelector('input[type="radio"]:checked'));
    }
    if (group.type === 'select') {
      return Boolean(group.el.value && String(group.el.value).trim());
    }
    return false;
  }

  /**
   * Personalization: required fields must be filled.
   * If none are required (all optional), shopper must interact with that block so empty optional fields do not read as "done" on load.
   */
  function resolvePersonalizationBox(form) {
    if (!form || !form.id) {
      return null;
    }
    const mainHandoff = form.querySelector('.product-personalization--main-handoff');
    if (mainHandoff) {
      return mainHandoff;
    }
    const sticky = document.querySelector('.product-single__stick-add .product-personalization--sticky');
    if (sticky) {
      return sticky;
    }
    return form.querySelector('[data-product-personalization]');
  }

  function isPersonalizationStepComplete(form, personalizationEngaged) {
    const box = resolvePersonalizationBox(form);
    if (!box) {
      return true;
    }
    const inputs = box.querySelectorAll('input.product-personalization__input-field');
    if (!inputs.length) {
      return true;
    }
    const required = Array.from(inputs).filter(function (i) {
      return i.required;
    });
    if (required.length) {
      return required.every(function (i) {
        return i.value.trim() !== '';
      });
    }
    return personalizationEngaged;
  }

  function computeVariantStepsDone(variantGroups, variantTouched) {
    let done = 0;
    for (let i = 0; i < variantGroups.length; i++) {
      const g = variantGroups[i];
      const touchIndex = g.type === 'radio' ? parseInt(g.el.getAttribute('data-option-index'), 10) : i;
      if (Number.isNaN(touchIndex) || touchIndex < 0 || touchIndex >= variantTouched.length) {
        continue;
      }
      if (variantTouched[touchIndex] && isVariantGroupDone(g)) {
        done += 1;
      }
    }
    return done;
  }

  /**
   * Theme mobile tab bar (#stickymenu_bottom_mobile) is fixed bottom; reserve its height so the
   * options progress bar sits above it and scroll padding clears both layers.
   */
  function getMobileStickyMenuBottomOffset() {
    if (window.matchMedia('(min-width: 768px)').matches) {
      return 0;
    }
    const bar = document.getElementById('stickymenu_bottom_mobile');
    if (!bar) {
      return 0;
    }
    const st = window.getComputedStyle(bar);
    if (st.display === 'none' || st.visibility === 'hidden') {
      return 0;
    }
    const h = Math.ceil(bar.getBoundingClientRect().height);
    return h > 0 ? h : 0;
  }

  function initRoot(root) {
    if (root.dataset.progressInit === 'true') {
      return;
    }

    const sectionId = root.getAttribute('data-section-id');
    if (!sectionId) {
      return;
    }
    const form = document.getElementById('product-form-' + sectionId);
    if (!form) {
      return;
    }

    root.dataset.progressInit = 'true';

    const totalSteps = parseInt(root.getAttribute('data-total-steps'), 10) || 0;
    const expectedVariantGroups = parseInt(root.getAttribute('data-variant-option-count'), 10) || 0;
    const hasPersonalizationStep = root.getAttribute('data-has-personalization') === 'true';
    const showPercent = root.getAttribute('data-show-percent') !== 'false';
    const showNextStep = root.getAttribute('data-show-next-step') !== 'false';
    const pulseAtc = root.getAttribute('data-pulse-atc') === 'true';
    const stepOuterGlow = root.getAttribute('data-step-outer-glow') !== 'false';

    const variantTouched = new Array(expectedVariantGroups).fill(false);
    let personalizationEngaged = false;

    const fill = root.querySelector('.product-options-progress__fill');
    const percentEl = root.querySelector('.product-options-progress__percent');
    const nextStepEl = root.querySelector('[data-next-step-text]');
    const note = root.querySelector('.product-options-progress__note');
    const noteLead = root.querySelector('[data-options-progress-note-lead]');
    const noteTail = root.querySelector('[data-options-progress-note-tail]');
    const segments = root.querySelectorAll('.product-options-progress__segment');

    const addBtn = form.querySelector('.product-form__cart-submit, button[name="add"]');
    const stickyAtc = root.querySelector('[data-options-progress-sticky-atc]');
    const stickyMobileAtcEnabled = root.getAttribute('data-sticky-mobile-atc') === 'true';
    const slot = root.closest('[data-options-progress-slot]');

    let hintsPayload = { steps: [], complete: '' };
    if (showNextStep) {
      const hintScript = root.querySelector('[data-options-progress-hints-json]');
      if (hintScript && hintScript.textContent) {
        try {
          hintsPayload = normalizeHintsPayload(JSON.parse(hintScript.textContent.trim()));
        } catch (err) {
          hintsPayload = { steps: [], complete: '' };
        }
      }
    }

    let pinFrame = null;
    function syncMobilePin() {
      const docEl = document.documentElement;

      function clearPinLayout() {
        docEl.style.removeProperty('--options-progress-bottom-pad');
        docEl.style.removeProperty('--stickymenu-mobile-bottom-offset');
        docEl.style.removeProperty('--options-progress-top');
        if (slot) {
          slot.style.minHeight = '';
        }
      }

      if (!root.classList.contains('product-options-progress--sticky-mobile')) {
        clearPinLayout();
        return;
      }
      if (window.matchMedia('(min-width: 768px)').matches) {
        clearPinLayout();
        return;
      }
      if (slot) {
        slot.style.minHeight = '';
      }
      window.requestAnimationFrame(function () {
        const menuOffset = getMobileStickyMenuBottomOffset();
        docEl.style.setProperty('--stickymenu-mobile-bottom-offset', menuOffset + 'px');
        const h = Math.ceil(root.getBoundingClientRect().height);
        docEl.style.setProperty('--options-progress-bottom-pad', h + menuOffset + 'px');
      });
    }

    function scheduleSyncMobilePin() {
      if (pinFrame !== null) {
        return;
      }
      pinFrame = window.requestAnimationFrame(function () {
        pinFrame = null;
        syncMobilePin();
      });
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (fill && reduceMotion) {
      fill.style.transition = 'none';
    }

    function findFirstIncompleteTarget(variantGroups) {
      if (totalSteps <= 0) {
        return null;
      }
      for (let i = 0; i < variantGroups.length; i++) {
        const g = variantGroups[i];
        const touchIndex = g.type === 'radio' ? parseInt(g.el.getAttribute('data-option-index'), 10) : i;
        const idxOk =
          !Number.isNaN(touchIndex) && touchIndex >= 0 && touchIndex < variantTouched.length;
        const complete = idxOk && variantTouched[touchIndex] && isVariantGroupDone(g);
        if (!complete) {
          return g.el.closest('.product-form__option-surface') || g.el;
        }
      }
      if (hasPersonalizationStep) {
        const box = resolvePersonalizationBox(form);
        if (box && !isPersonalizationStepComplete(form, personalizationEngaged)) {
          return box;
        }
      }
      return null;
    }

    function syncStepHighlight(variantGroups) {
      form.querySelectorAll('.product-options-progress-step--active').forEach(function (n) {
        n.classList.remove('product-options-progress-step--active', 'product-options-progress-step--outer-glow');
        n.removeAttribute('aria-current');
      });
      const target = findFirstIncompleteTarget(variantGroups);
      if (target) {
        target.classList.add('product-options-progress-step--active');
        if (stepOuterGlow) {
          target.classList.add('product-options-progress-step--outer-glow');
        }
        target.setAttribute('aria-current', 'step');
      }
    }

    function computeDoneSteps(variantGroups) {
      let done = 0;
      if (expectedVariantGroups === 0) {
        if (hasPersonalizationStep && isPersonalizationStepComplete(form, personalizationEngaged)) {
          done = 1;
        }
      } else {
        done = computeVariantStepsDone(variantGroups, variantTouched);
        if (hasPersonalizationStep && isPersonalizationStepComplete(form, personalizationEngaged)) {
          done += 1;
        }
      }
      return done;
    }

    function markEngagementFromTarget(target) {
      if (!target || target.nodeType !== 1) {
        return;
      }
      if (target.closest && target.closest('[data-product-personalization]')) {
        personalizationEngaged = true;
      }
      if (!form.contains(target)) {
        if (!(target.closest && target.closest('.product-single__stick-add'))) {
          return;
        }
      }
      const fs = target.closest && target.closest('fieldset.product-form__input[data-option-index]');
      if (fs) {
        const idx = parseInt(fs.getAttribute('data-option-index'), 10);
        if (!Number.isNaN(idx) && idx >= 0 && idx < variantTouched.length) {
          variantTouched[idx] = true;
        }
      }
      if (target.matches && target.matches('select') && target.closest('variant-selects')) {
        const vs = target.closest('variant-selects');
        const selects = vs.querySelectorAll('select');
        const index = Array.prototype.indexOf.call(selects, target);
        if (index >= 0 && index < variantTouched.length) {
          variantTouched[index] = true;
        }
      }
      if (target.matches && target.matches('select') && target.closest('variant-selects-stick')) {
        const vs = target.closest('variant-selects-stick');
        const selects = vs.querySelectorAll('select');
        const index = Array.prototype.indexOf.call(selects, target);
        if (index >= 0 && index < variantTouched.length) {
          variantTouched[index] = true;
        }
      }
    }

    function update() {
      const variantGroups = getVariantGroups(form);
      const done = computeDoneSteps(variantGroups);

      const total = totalSteps > 0 ? totalSteps : 1;
      const ratio = Math.min(done / total, 1);
      const pct = Math.round(ratio * CAP);

      if (fill) {
        fill.style.width = pct + '%';
      }

      root.setAttribute('aria-valuenow', String(pct));

      let nextStepText = '';
      if (showNextStep && nextStepEl) {
        const steps = hintsPayload.steps || [];
        if (total > 0 && done >= total) {
          nextStepText = hintsPayload.complete || '';
        } else if (done < steps.length) {
          nextStepText = steps[done] || '';
        }
        nextStepEl.textContent = nextStepText;
        nextStepEl.hidden = !nextStepText;
      }

      const ariaPieces = [];
      if (showPercent) {
        ariaPieces.push(pct + '%');
      }
      if (nextStepText) {
        ariaPieces.push(nextStepText);
      }
      if (ariaPieces.length === 0) {
        ariaPieces.push(String(done) + ' of ' + String(total) + ' steps');
      }
      root.setAttribute('aria-valuetext', ariaPieces.join('. '));

      if (percentEl) {
        percentEl.textContent = showPercent ? pct + '%' : '';
        percentEl.hidden = !showPercent;
      }

      segments.forEach(function (seg, i) {
        seg.classList.toggle('product-options-progress__segment--complete', i < done);
      });

      const allComplete = done >= total && total > 0;
      if (note) {
        note.hidden = !allComplete;
      }
      if (noteLead) {
        noteLead.hidden = !allComplete;
      }
      if (noteTail) {
        noteTail.hidden = !allComplete;
      }

      if (addBtn && pulseAtc) {
        addBtn.classList.toggle('product-options-progress__atc-highlight', allComplete);
      }

      if (stickyAtc) {
        if (addBtn) {
          stickyAtc.disabled = Boolean(addBtn.disabled);
        } else {
          stickyAtc.disabled = true;
        }
      }

      syncStepHighlight(variantGroups);

      scheduleSyncMobilePin();
    }

    function onEngagementEvent(e) {
      markEngagementFromTarget(e.target);
      update();
    }

    form.addEventListener('pointerdown', onEngagementEvent, true);
    form.addEventListener('focusin', onEngagementEvent);
    form.addEventListener('change', onEngagementEvent);
    form.addEventListener('input', onEngagementEvent);

    const stickAddBar = document.querySelector('.product-single__stick-add');
    if (stickAddBar) {
      stickAddBar.addEventListener('pointerdown', onEngagementEvent, true);
      stickAddBar.addEventListener('focusin', onEngagementEvent);
      stickAddBar.addEventListener('change', onEngagementEvent);
      stickAddBar.addEventListener('input', onEngagementEvent);
    }

    if (stickyAtc && stickyMobileAtcEnabled) {
      stickyAtc.addEventListener('click', function () {
        if (stickyAtc.disabled) {
          return;
        }
        const variantGroups = getVariantGroups(form);
        const done = computeDoneSteps(variantGroups);
        const total = totalSteps > 0 ? totalSteps : 1;
        const allComplete = done >= total && total > 0;
        if (!allComplete) {
          const el = findFirstIncompleteTarget(variantGroups);
          if (el && typeof el.scrollIntoView === 'function') {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            el.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
            const tryFocus = function () {
              const focusable = el.querySelector(
                'input:not([type="hidden"]), select, textarea, button, [href], [tabindex]:not([tabindex="-1"])'
              );
              if (focusable && typeof focusable.focus === 'function') {
                focusable.focus({ preventScroll: true });
              }
            };
            if (reduceMotion) {
              tryFocus();
            } else {
              window.setTimeout(tryFocus, 350);
            }
          }
          return;
        }
        if (addBtn) {
          addBtn.click();
        }
      });
    }

    window.addEventListener('scroll', scheduleSyncMobilePin, { passive: true });
    window.addEventListener('resize', scheduleSyncMobilePin);
    document.addEventListener('shopify:section:load', function (ev) {
      if (ev.detail && ev.detail.sectionId === sectionId) {
        update();
        scheduleSyncMobilePin();
      }
    });

    update();
    scheduleSyncMobilePin();
  }

  function boot() {
    document.querySelectorAll('[data-product-options-progress-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
